import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'legal@autopilot.co.zw';

const steps = [
  {
    number: '01',
    title: 'Submit your request',
    body: `Email ${companyEmail} with the subject line "Data Deletion Request". Include your full name and the email address linked to your account. If you connected via Facebook Login, also include your Facebook name so we can locate the associated record.`,
  },
  {
    number: '02',
    title: 'Identity verification',
    body: 'We will reply within 3 business days to verify your identity. This step protects you — it ensures no one else can delete your data on your behalf.',
  },
  {
    number: '03',
    title: 'Deletion confirmed',
    body: 'Once verified, we will permanently delete your personal data within 30 days and send you a confirmation email. Some financial records may be retained for up to 7 years as required by Zimbabwean tax law.',
  },
];

const dataTypes = [
  { icon: '👤', label: 'Account profile', detail: 'Name, email, phone number, business details' },
  { icon: '🤖', label: 'Bot configurations', detail: 'Flow data, settings, API credentials' },
  { icon: '💬', label: 'Conversation history', detail: 'All messages exchanged through your bots' },
  { icon: '👥', label: 'Contact lists', detail: 'End-user phone numbers and conversation records' },
  { icon: '🔗', label: 'Facebook / Meta data', detail: 'Linked account tokens and page identifiers' },
  { icon: '📊', label: 'Analytics data', detail: 'Usage statistics and reporting history' },
];

export default function DataDeletion() {
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText(companyEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <SEO
        title={`Data Deletion | ${companyName}`}
        description={`Request deletion of your personal data from the ${companyName} platform. We will complete your request within 30 days.`}
        keywords="data deletion, privacy, GDPR, data erasure, right to be forgotten"
        type="website"
      />

      <style>{`
        * { box-sizing: border-box; }
        .step-card { transition: border-color 0.2s; }
        .step-card:hover { border-color: rgba(91,79,232,0.4) !important; }
        .copy-btn { transition: background 0.15s; }
        .copy-btn:hover { background: rgba(91,79,232,0.25) !important; }
        @media (max-width: 768px) {
          .steps-grid { grid-template-columns: 1fr !important; }
          .data-grid { grid-template-columns: 1fr !important; }
          .hero-title { font-size: 28px !important; }
        }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0f1117', color: 'white', minHeight: '100vh' }}>

        {/* Nav */}
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '60px', background: 'rgba(15,17,23,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1e2330', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Privacy</Link>
            <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Terms</Link>
          </div>
        </nav>

        {/* Hero */}
        <div style={{ paddingTop: '110px', paddingBottom: '64px', paddingLeft: '32px', paddingRight: '32px', textAlign: 'center', background: 'linear-gradient(135deg, #0f1117 0%, #12111a 100%)', borderBottom: '1px solid #1e2330' }}>
          <div style={{ maxWidth: '640px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '20px' }}>YOUR RIGHTS</div>
            <h1 className="hero-title" style={{ fontSize: '38px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '16px', lineHeight: 1.15 }}>Request Your Data Deletion</h1>
            <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.7', marginBottom: '0' }}>
              You have the right to request that {companyName} permanently deletes all personal data we hold about you. We will complete your request within 30 days.
            </p>
          </div>
        </div>

        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '64px 32px' }}>

          {/* Steps */}
          <div style={{ marginBottom: '64px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', marginBottom: '28px' }}>How it works</h2>
            <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
              {steps.map(s => (
                <div key={s.number} className="step-card" style={{ background: '#15181f', border: '1px solid #1e2330', borderRadius: '14px', padding: '24px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: 'rgba(91,79,232,0.4)', marginBottom: '12px', letterSpacing: '-0.02em' }}>{s.number}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0', marginBottom: '10px' }}>{s.title}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.7' }}>{s.body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* What gets deleted */}
          <div style={{ marginBottom: '64px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px' }}>What data gets deleted</h2>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '24px' }}>A full deletion request removes all of the following from our systems:</p>
            <div className="data-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {dataTypes.map(d => (
                <div key={d.label} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: '#15181f', border: '1px solid #1e2330', borderRadius: '12px', padding: '16px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>{d.icon}</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#e2e8f0', marginBottom: '3px' }}>{d.label}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{d.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What we may retain */}
          <div style={{ marginBottom: '64px', padding: '24px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '14px' }}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '22px' }}>⚠️</span>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#fbbf24', marginBottom: '8px' }}>Data retained for legal compliance</div>
                <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.7', margin: 0 }}>
                  Financial records (invoices and payment history) may be retained for up to 7 years as required by Zimbabwean tax law. This data is isolated from active systems and not used for any other purpose. All other data — including bot flows, conversations, contacts, and Facebook tokens — will be permanently deleted.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center', padding: '48px', background: '#15181f', border: '1px solid #1e2330', borderRadius: '20px' }}>
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>📧</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px' }}>Send your deletion request</h3>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '24px' }}>Email us from the address linked to your account with the subject line <strong style={{ color: '#94a3b8' }}>"Data Deletion Request"</strong></p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <a
                href={`mailto:${companyEmail}?subject=Data%20Deletion%20Request`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#5B4FE8', color: 'white', textDecoration: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '600' }}
              >
                Open email client
              </a>
              <button
                className="copy-btn"
                onClick={copyEmail}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(91,79,232,0.15)', color: '#a5b4fc', border: '1px solid rgba(91,79,232,0.3)', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
              >
                {copied ? '✓ Copied' : `Copy: ${companyEmail}`}
              </button>
            </div>
          </div>

          {/* Footer links */}
          <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid #1e2330', display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/privacy" style={{ color: '#64748b', fontSize: '13px', textDecoration: 'none' }}>Privacy Policy</Link>
            <Link to="/terms" style={{ color: '#64748b', fontSize: '13px', textDecoration: 'none' }}>Terms of Service</Link>
            <Link to="/privacy#your-rights" style={{ color: '#64748b', fontSize: '13px', textDecoration: 'none' }}>Your Rights</Link>
            <a href={`mailto:${companyEmail}`} style={{ color: '#64748b', fontSize: '13px', textDecoration: 'none' }}>{companyEmail}</a>
          </div>
        </div>
      </div>
    </>
  );
}
